import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth.store'
import {
  Activity,
  ArrowRight,
  HeartHandshake,
  LogOut,
  Menu,
  MessageSquareText,
  ShieldCheck,
  Sparkles,
  UserRoundCheck,
  Users,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'

const navItems = [
  {
    name: 'Operations',
    label: 'Health & outcomes',
    href: '/dashboard',
    icon: Activity,
  },
  {
    name: 'Matching queue',
    label: 'Review new requests',
    href: '/matching',
    icon: Sparkles,
  },
  {
    name: 'Candidates',
    label: 'Assignments & support',
    href: '/candidates',
    icon: UserRoundCheck,
  },
  {
    name: 'Coach bench',
    label: 'Coverage & capacity',
    href: '/coaches',
    icon: Users,
  },
  {
    name: 'Coach review',
    label: 'Profiles & credentials',
    href: '/coach-review',
    icon: ShieldCheck,
  },
  {
    name: 'Reviews',
    label: 'Chemistry & reflections',
    href: '/reviews',
    icon: MessageSquareText,
  },
]

export function CoachManagerLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const { clearAuth, user } = useAuthStore()
  const handleLogout = () => {
    clearAuth()
    navigate('/login')
  }
  const sidebar = (
    <>
      <div className="flex h-20 items-center justify-between border-b border-white/10 px-6">
        <Link
          to="/matching"
          className="flex items-center gap-3"
          onClick={() => setMobileOpen(false)}
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-400 text-[#07152e] shadow-lg shadow-sky-950/20">
            <HeartHandshake className="h-5 w-5" />
          </span>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-300">
              Pelgo
            </p>
            <p className="text-sm font-semibold text-white">Coach matching</p>
          </div>
        </Link>
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="rounded-lg p-2 text-slate-400 lg:hidden"
          aria-label="Close navigation"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="px-4 pt-7">
        <p className="px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          Operations
        </p>
        <nav className="mt-3 space-y-1.5">
          {navItems.map((item) => {
            const Icon = item.icon
            const active = location.pathname === item.href
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  'group flex items-center gap-3 rounded-2xl px-3 py-3 transition',
                  active
                    ? 'bg-white/[0.09] text-white shadow-inner shadow-white/5'
                    : 'text-slate-400 hover:bg-white/[0.05] hover:text-white',
                )}
              >
                <span
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-xl',
                    active
                      ? 'bg-sky-400 text-[#07152e]'
                      : 'bg-white/[0.05] text-slate-500 group-hover:text-slate-300',
                  )}
                >
                  <Icon className="h-[18px] w-[18px]" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{item.name}</span>
                  <span className="block text-[11px] text-slate-500">{item.label}</span>
                </span>
                {active && <ArrowRight className="h-3.5 w-3.5 text-sky-300" />}
              </Link>
            )
          })}
        </nav>
      </div>
      <div className="mx-4 mt-8 rounded-2xl border border-sky-300/15 bg-gradient-to-br from-sky-400/10 to-indigo-400/5 p-4">
        <p className="text-xs font-semibold text-sky-200">Human in the loop</p>
        <p className="mt-2 text-xs leading-5 text-slate-400">
          The system explains the fit. You make the final call.
        </p>
      </div>
      <div className="mt-auto border-t border-white/10 p-4">
        <div className="flex items-center gap-3 rounded-2xl p-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-100 text-sm font-bold text-[#07152e]">
            {(user?.first_name?.[0] || user?.email?.[0] || 'M').toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">
              {user?.full_name || user?.first_name || 'Coach manager'}
            </p>
            <p className="truncate text-[11px] text-slate-500">Matching team</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="rounded-xl p-2 text-slate-500 hover:bg-white/10 hover:text-white"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  )
  return (
    <div className="min-h-screen bg-[#f6f7f4] text-slate-950 lg:flex">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[280px] flex-col bg-[#07152e] lg:flex">
        {sidebar}
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          />
          <aside className="relative flex h-full w-[min(86vw,300px)] flex-col bg-[#07152e] shadow-2xl">
            {sidebar}
          </aside>
        </div>
      )}
      <div className="min-w-0 flex-1 lg:ml-[280px]">
        <header className="sticky top-0 z-30 flex h-16 items-center border-b border-slate-200/70 bg-[#f6f7f4]/90 px-4 backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="rounded-xl border border-slate-200 bg-white p-2"
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="ml-3 text-sm font-semibold text-[#07152e]">Coach matching</span>
        </header>
        <main className="mx-auto min-h-screen w-full max-w-[1480px] px-4 py-7 sm:px-7 lg:px-10 lg:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
